import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import ConnectedMap from 'app/components/ConnectedMap';
import {RenderMapCallbackOpts} from 'app/components/ConnectedMap/view';
import FeatureCollectionVector from 'app/components/DeclarativeMap/FeatureCollectionVector';
import FeatureRaster from 'app/components/DeclarativeMap/FeatureRaster';
import GeoJsonFitter from 'app/components/DeclarativeMap/GeoJsonFitter';
import MapContent from 'app/components/DeclarativeMap/MapContent';
import MapStyle from 'app/components/DeclarativeMap/MapStyle';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {WithMonitoringFeatures} from 'app/pages/ProjectDashboardSwitch/view';
import colors from 'app/styles/colors.json';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as makeLayers from 'app/utils/mapUtils/makeLayers';

interface ObservationMapProps {
  imageRefs: mapUtils.MapImageRefs;
  selectedFeature: I.ImmutableOf<ApiFeature>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  organization: I.ImmutableOf<ApiOrganization>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureIdParams: I.Set<string>;
  refreshFeatureCollection: (featureCollectionId: number) => Promise<void> | void;
  noteGeometry: geojson.Geometry;
  firebaseToken: string;
}

export const ObservationMap = ({
  imageRefs,
  selectedFeature,
  selectedFeatureCollection,
  organization,
  selectedProject,
  selectedFeatureIdParams,
  refreshFeatureCollection,
  noteGeometry,
  firebaseToken,
}: ObservationMapProps) => {
  // todo(eva): would be sick to also pass in all notes and have a mode where we
  // can view all note geoms at once and then click on the one we want to see
  const focusedGeometry = React.useMemo(() => {
    return noteGeometry;
  }, [noteGeometry]);

  const fitBoundsOptions = {
    padding: 100,
    maxZoom: 18,
    duration: 1000,
  };

  return (
    <WithMonitoringFeatures
      selectedProject={selectedProject}
      selectedFeatureCollection={selectedFeatureCollection}
      selectedFeatureIdParams={selectedFeatureIdParams}
      refreshFeatureCollection={refreshFeatureCollection}
    >
      {({selectedFeatures, featureDataActions, features}) => {
        const selectedFeatureIds = I.Set([selectedFeature.get('id')]);

        // Get feature data for the selected feature
        const featureDataMaybe = featureDataActions.getFeatureData(selectedFeature.get('id'));
        const featureData = featureDataMaybe?.status === 'some' ? featureDataMaybe.value : null;
        // Create hydrated feature collection
        const hydratedFeatureCollection = featureCollectionUtils.hydratedFeatureCollection(
          selectedFeatureCollection,
          features
        );

        return (
          <ConnectedMap
            imageRefs={imageRefs}
            featureData={featureData}
            mode={imageRefs.length > 1 ? 'COMPARE' : 'VIEW'}
            firebaseToken={firebaseToken}
            selectedFeatureCollection={hydratedFeatureCollection}
            selectedFeatureIds={selectedFeatureIds}
            selectedFeatures={selectedFeatures}
            organization={organization}
            onMapClick={() => {}}
            controlPosition="top-right"
          >
            {({map, isMapLoaded, cursor, layerKey}: RenderMapCallbackOpts) => {
              if (map) map.scrollZoom.disable();
              return (
                <>
                  <MapStyle map={map} style={mapUtils.MAPBOX_DARK_STYLE} />
                  <FeatureRaster
                    map={map}
                    isMapLoaded={isMapLoaded}
                    activeLayerKey={layerKey}
                    cursor={cursor}
                    featureCollection={hydratedFeatureCollection}
                    featureData={featureData || I.List([])}
                  />
                  <GeoJsonFitter
                    map={map}
                    geoJson={focusedGeometry}
                    fitBoundsOptions={fitBoundsOptions}
                  />
                  <NoteGeometryRenderer
                    map={map}
                    isMapLoaded={isMapLoaded}
                    geometry={noteGeometry}
                  />
                  {map && (
                    <FeatureCollectionVector
                      map={map}
                      isMapLoaded={isMapLoaded}
                      featureCollection={hydratedFeatureCollection}
                      selectedFeatureIds={selectedFeatureIds}
                      // we're viewing this out of the context of the full feature collection
                      selectFeatureById={() => {}}
                      hoveredFeatureIds={I.Set([])}
                      hoverFeatureById={() => {}}
                      showPropertyBoundaries={true}
                    />
                  )}
                </>
              );
            }}
          </ConnectedMap>
        );
      }}
    </WithMonitoringFeatures>
  );
};

// this is a very basic version of how we render note geoms on MonitorProjectView
// 'cause we dont need to haul around all the notes state for existing notes
const NoteGeometryRenderer: React.FC<{
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  geometry: geojson.Geometry;
}> = ({map, isMapLoaded, geometry}) => {
  const sourceId = 'note-geometry-source';
  const layerId = 'note-geometry-layer';

  const source = React.useMemo(
    () => ({
      id: sourceId,
      source: {
        type: 'geojson' as const,
        data: geoJsonUtils.feature(geometry, {}),
      },
    }),
    [geometry]
  );

  const layer = React.useMemo(
    () => ({
      id: layerId,
      maxzoom: 22,
      minzoom: 2,
      ...makeLayers.makeLine({
        color: colors.yellow, // Use the same yellow color as user notes
        width: 4,
        opacity: 1,
      }),
      source: sourceId,
    }),
    [sourceId]
  );

  return <MapContent map={map} isMapLoaded={isMapLoaded} sources={[source]} layers={[layer]} />;
};
